import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { takeUntil, take, tap } from 'rxjs/operators';
import { OrdersService } from './orders.service';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { IOSimPlusIntegrationService } from 'app/services/iosim-plus-integration/iosim-plus-integration.service';
import { ShellContextService } from 'app/services/shell-context/shell-context.service';
import { FeaturesToggleSettingsService } from '../../featuresToggleSettings/service/featuresToggleSettings.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { ChangeCaseTypeModal } from '@shared/changeCaseType/changeCaseType.component';
import { ExocadMiniAppModal } from '@shared/exocadMiniAppModal/exocadMiniAppModal.component';
import { DownloadLinkModal } from '@shared/downloadLinkModal/downloadLinkModal.component';
import { OrthoCadExportService } from '@shared/orthoCadExport/orthoCadExport.service';
import { RestorativeExportService } from '@shared/restorativeExport/restorativeExport.service';
import { VisitReportComponent } from '../../visit-report/visit-report.component';
import { KnownComponentsNames } from '@core/components-names';
import { FeatureToggle, SoftwareOptionsForCompany } from '@shared/enums';
import { Pages } from '@logging/interfaces/bi.enums';
import { SimulationStatusEnum } from '@shared/iosim-plus/models/simulationStatus';
import { WindowsApplications } from '@shared/windowsAppButton/WindowsApplications';
import { Iframes } from 'app/rx/enums/Iframes';
import { STANDARD_REMOTE_URL_PREFIX } from 'app/remote-application/consts';
import { Consts } from '@shared/consts';
import { TimberService } from '@logging/timber.service';
import { BaseDestroyable } from '@core/base-destroyable';
import { filter } from 'rxjs/operators';
import { OrdersButtonAction } from '@itero/itero-client-communication';
import { IOSimSimulationStatusProgressService } from 'app/services/iosim-simulation.store/iosim-simulation-status-progress.service';

/**
 * Payload shape received from the `orders-button-clicked` event emitted by `orders-ui`.
 * Mirrors `OrdersButtonClicked['data']` from `@itero/itero-client-communication`.
 */
export interface OrdersButtonClickedPayload {
	action: OrdersButtonAction;
	orderId: number;
	rxId?: string;
	isTakenByScanner?: boolean;
	isReadOnly?: boolean;
	statusId?: number;
	caseTypeId?: number;
	procedureId?: number;
	orderDetailsId?: number;
	orderCode?: string;
	isOrthoOrder?: boolean;
	patientName?: string;
	companyId?: number;
	isCaptureFileExist?: boolean;
	isScanImagesZipFileExist?: boolean;
	ioSimPlusEnabled?: boolean;
	isDesignShared?: boolean;
	simulationStatus?: number;
	expectedDuration?: number;
	startSimulationTime?: string;
	notSupportedScanDate?: boolean;
	isQualifiedForIoSimPlus?: boolean;
	openFileLink?: string;
}

/**
 * Handles navigation and modal actions triggered by `orders-button-clicked` events
 * emitted from the `itero-orders-ui` MFE. Extracted from `OrdersComponent` so the
 * shell event handler can invoke the same logic without coupling a service to a component.
 *
 * Modal references (`changeCaseTypeModal`, `visitReportComponent`) must be set by the host
 * `OrdersComponent` after its view is initialised (via `setModalRefs`). Actions that require
 * modals are silently skipped if the refs are not yet available.
 *
 * Export modals are opened via injected services (`OrthoCadExportService`, `RestorativeExportService`)
 * which use the modal window service internally.
 */
@Injectable({ providedIn: 'root' })
export class OrdersNavigationService extends BaseDestroyable {
	private changeCaseTypeModal: ChangeCaseTypeModal | null = null;
	private visitReportComponent: VisitReportComponent | null = null;
	private exocadMiniAppModal: ExocadMiniAppModal | null = null;
	private downloadLinkModal: DownloadLinkModal | null = null;

	constructor(
		private readonly router: Router,
		private readonly ordersService: OrdersService,
		private readonly applicationNavigationService: ApplicationNavigationService,
		private readonly iOSimPlusIntegrationService: IOSimPlusIntegrationService,
		private readonly shellContextService: ShellContextService,
		private readonly featuresToggleSettingsService: FeaturesToggleSettingsService,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly globalSettingsService: GlobalSettingsService,
		private readonly timberService: TimberService,
		private readonly orthoCadExportService: OrthoCadExportService,
		private readonly restorativeExportService: RestorativeExportService,
		private readonly eupRoutesService: EupRoutesService,
		private readonly simulationStatusProgressService: IOSimSimulationStatusProgressService,
	) {
		super();
	}

	/**
	 * Called by `OrdersComponent.ngAfterViewInit` to wire up the modal `@ViewChild` refs.
	 * These cannot be injected as services because they are component-scoped.
	 */
	setModalRefs(refs: {
		changeCaseTypeModal?: ChangeCaseTypeModal;
		visitReportComponent?: VisitReportComponent;
		exocadMiniAppModal?: ExocadMiniAppModal;
		downloadLinkModal?: DownloadLinkModal;
	}): void {
		// Only update refs that are explicitly provided, preserve existing values otherwise
		if (refs.changeCaseTypeModal !== undefined) {
			this.changeCaseTypeModal = refs.changeCaseTypeModal;
		}
		if (refs.visitReportComponent !== undefined) {
			this.visitReportComponent = refs.visitReportComponent;
		}
		if (refs.exocadMiniAppModal !== undefined) {
			this.exocadMiniAppModal = refs.exocadMiniAppModal;
		}
		if (refs.downloadLinkModal !== undefined) {
			this.downloadLinkModal = refs.downloadLinkModal;
		}
	}

	/** Dispatch an action from the `orders-button-clicked` event payload. */
	handleButtonClicked(payload: OrdersButtonClickedPayload): void {
		switch (payload.action) {
			case OrdersButtonAction.ViewRx:
				this.navigateToRx(payload);
				break;
			case OrdersButtonAction.AddAttachments:
				this.navigateToRxAttachments(payload);
				break;
			case OrdersButtonAction.OpenViewer:
				this.navigateToViewer(payload);
				break;
			case OrdersButtonAction.OpenPoiViewer:
				this.navigateToViewer(payload, { showPOIViewer: true, locale: this.globalSettingsService.get()?.selectedLanguage?.code });
				break;
			case OrdersButtonAction.OpenAohs:
				this.openAohs(payload.orderId);
				break;
			case OrdersButtonAction.OpenIosim:
				this.openIosim(payload);
				break;
			case OrdersButtonAction.ChangeCaseType:
				this.openChangeCaseTypeModal(payload);
				break;
			case OrdersButtonAction.Export:
				this.openExportModal(payload);
				break;
			case OrdersButtonAction.ExportGallery:
				this.downloadGalleryImages(payload.orderId, payload.rxId);
				break;
			case OrdersButtonAction.OpenExocad:
				this.navigateToViewer(payload, {}, Iframes.Exocad3dViewer);
				break;
			case OrdersButtonAction.OpenVisitReport:
				this.showVisitReport(payload.orderId);
				break;
			case OrdersButtonAction.Send:
				this.sendPendingAttachments(payload.orderId);
				break;
			case OrdersButtonAction.DeleteRx:
				this.deleteRx(payload.rxId);
				break;
			case OrdersButtonAction.DownloadReferral:
				this.downloadReferralPdf(payload.rxId);
				break;
			case OrdersButtonAction.DownloadScreenshots:
				this.downloadScreenshots(payload.orderId, payload.patientName, payload.rxId);
				break;
			case OrdersButtonAction.OpenIteroDesignSuite:
				this.openIteroDesignSuite(payload);
				break;
			case OrdersButtonAction.DownloadDesignFiles:
				this.downloadDesignFiles(payload.orderId, payload.rxId);
				break;
			case OrdersButtonAction.OpenOrthocad:
				this.openOrthoCad(payload);
				break;
			// Resend action - using string literal until @itero/itero-client-communication is updated
			case 'resend' as OrdersButtonAction:
				this.navigateToResend(payload);
				break;
			default:
				this.timberService.warn(
					`OrdersNavigationService: unrecognised action '${(payload as any).action}'`,
					{ module: 'OrdersNavigationService' }
				);
		}
	}

	navigateToRx(payload: OrdersButtonClickedPayload): void {
		const { rxId, orderId, isTakenByScanner, isReadOnly, statusId } = payload;
		// When statusId === 10 (pending attachments) the guid is used as the route id.
		// Use || (falsy check) not ?? (nullish check) so that orderId=0 (rx forms that have
		// no real order id yet) also falls back to rxId, matching the legacy behaviour:
		// (rowData.id || rowData.guid) in PatientsOrdersComponent.navigateToRx.
		const id = statusId === 10 ? rxId : (orderId || rxId);
		const navigateOptions: NavigationExtras = {
			queryParams: {
				isReadOnly: isReadOnly ?? false,
				statusId,
			},
			state: { ViewerSourceUrl: this.router.url },
		};
		this.router.navigate(['/rx', id, isTakenByScanner ?? true], navigateOptions);
	}

	navigateToViewer(payload: OrdersButtonClickedPayload, additionalParams: Record<string, unknown> = {}, app = Iframes.Viewer): void {
		// Update the order object (needed by ScreenshotManager for saving screenshots)
		this.shellContextService.updateOrder({ id: payload.orderId, guid: payload.rxId });

		const queryParams = {
			rxId: payload.rxId,
			caseTypeId: payload.caseTypeId,
			procedureId: payload.procedureId,
			orderDetailsId: payload.orderDetailsId,
			orderId: payload.orderId,
			orderCode: payload.orderCode,
			isOrtho: payload.isOrthoOrder,
			isLabViewer: false,
			patientName: payload.patientName,
			isCaptureFileExist: payload.isCaptureFileExist,
			isScanImagesZipFileExist: payload.isScanImagesZipFileExist,
			expectedDuration: payload.expectedDuration,
			simulationStatus: payload.simulationStatus,
			startSimulationTime: payload.startSimulationTime,
			notSupportedScanDate: payload.notSupportedScanDate,
			ioSimPlusEnabled: payload.ioSimPlusEnabled,
			companyId: payload.companyId,
			hasExocadDesign: payload.isDesignShared,
			...additionalParams,
		};
		this.router.navigate([`/viewer-wrapper/${app}`, payload.orderId, true], {
			queryParams,
			state: { ViewerSourceUrl: this.router.url },
		});
	}

	openAohs(orderId: number): void {
		const isAohsUIFfEnabled = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.OpenAohsUI, false);
		const swOptions = this.globalSettingsService.get()?.companySoftwareOptions ?? [];
		const isAohsUISWOEnabled = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.AlignOralHealthSuiteClient, swOptions
		);
		const shouldOpenAohsUI = isAohsUIFfEnabled || isAohsUISWOEnabled;

		if (shouldOpenAohsUI) {
			this.ordersService
				.getBaseOrderData({ orderId })
				.pipe(
					take(1),
					tap((order) => {
						// Update shell context for business context (used by various services)
						this.shellContextService.updateContext((ctx) => {
							ctx.bizCtx.orderId = order.orderId;
							ctx.bizCtx.patientId = order.patientId;
							ctx.bizCtx.csmMessageId = order.caseStateMessageId;
							ctx.bizCtx.rxId = order.rxId;
						});
						// Also update the order object (needed by ScreenshotManager for saving screenshots)
						// ScreenshotManager only uses order.id, so we pass the minimal required fields
						this.shellContextService.updateOrder({ id: order.orderId, guid: order.rxId });
						
						const useRAL = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.EnableAohsRAL, false);
						if (useRAL) {
							this.applicationNavigationService.navigateToStandardRemoteApplication(KnownComponentsNames.AohsComponent);
						} else {
							this.router.navigate([`/mfe-wrapper/${KnownComponentsNames.AohsComponent}`]);
						}
					}),
					takeUntil(this.componentAlive$)
				)
				.subscribe();
		} else {
			// Fall back to viewer in 360-layout mode — best-effort without the full order object
			this.timberService.warn(
				`OrdersNavigationService: openAohs called but shouldOpenAohsUI=false and full order data unavailable; skipping.`,
				{ module: 'OrdersNavigationService' }
			);
		}
	}

	openIosim(payload: OrdersButtonClickedPayload): void {
		if (!payload.orderCode) {
			this.timberService.warn(
				'OrdersNavigationService: openIosim called without orderCode',
				{ module: 'OrdersNavigationService' }
			);
			return;
		}

		// Mimic legacy iosim-plus-button.component.ts performAction() behavior:
		// 1. Check qualification first
		// 2. Handle different simulation states appropriately

		// Check if order is not qualified or has unsupported scan date		
		if (payload.isQualifiedForIoSimPlus === false || payload.notSupportedScanDate) {
			// Add simulation info to Akita store so StatusInfoModalComponent can read it
			this.simulationStatusProgressService.addOrUpdateSimulationStatusProgress({
				orderId: String(payload.orderId),
				progress: {
					simulationStatus: payload.simulationStatus ?? SimulationStatusEnum.NotStarted,
					expectedDuration: payload.expectedDuration ?? IOSimPlusIntegrationService.defaultExpectedDuration,
					startSimulationTime: payload.startSimulationTime ?? null,
				},
				notSupportedScanDate: payload.notSupportedScanDate ?? false,
				isQualifiedForIoSimPlus: payload.isQualifiedForIoSimPlus ?? true,
			});
			this.iOSimPlusIntegrationService.showStatusInfoModal$.next({
				orderId: String(payload.orderId),
				orderCode: payload.orderCode,
				viewedScreen: Pages.Orders,
				isQualifiedForIoSimPlus: payload.isQualifiedForIoSimPlus ?? true,
			});
			return;
		}

		// Handle different simulation states		
		switch (payload.simulationStatus) {
			case SimulationStatusEnum.Ready:
				const transition = {
					navigatedFromAppId: KnownComponentsNames.IteroOrdersUi,
					urlFrom: this.router.url,
					navigatedToAppId: WindowsApplications.Iosim,
					appMetadata: JSON.stringify({ orderId: payload.orderId }),
				};
				this.iOSimPlusIntegrationService.iosimPlusTransition(transition);
				break;

			case SimulationStatusEnum.NotStarted:
				this.iOSimPlusIntegrationService.startSimulation(
					String(payload.orderId),
					payload.orderCode,
					Pages.Orders
				);
				break;

			case SimulationStatusEnum.LowQualityScan:
			case SimulationStatusEnum.Fail:
				// Add simulation info to Akita store so StatusInfoModalComponent can read it
				this.simulationStatusProgressService.addOrUpdateSimulationStatusProgress({
					orderId: String(payload.orderId),
					progress: {
						simulationStatus: payload.simulationStatus,
						expectedDuration: payload.expectedDuration ?? IOSimPlusIntegrationService.defaultExpectedDuration,
						startSimulationTime: payload.startSimulationTime ?? null,
					},
					notSupportedScanDate: payload.notSupportedScanDate ?? false,
					isQualifiedForIoSimPlus: payload.isQualifiedForIoSimPlus ?? true,
				});
				this.iOSimPlusIntegrationService.showStatusInfoModal$.next({
					orderId: String(payload.orderId),
					orderCode: payload.orderCode,
					viewedScreen: Pages.Orders,
					isQualifiedForIoSimPlus: payload.isQualifiedForIoSimPlus ?? true,
				});
				break;

			default:
				// For None (0), InProgress (2), or undefined status, use existing navigation behavior
				// This provides backward compatibility when simulationStatus is not provided
				const defaultTransition = {
					navigatedFromAppId: KnownComponentsNames.IteroOrdersUi,
					urlFrom: this.router.url,
					navigatedToAppId: WindowsApplications.Iosim,
					appMetadata: JSON.stringify({ orderId: payload.orderId }),
				};
				this.iOSimPlusIntegrationService.iosimPlusTransition(defaultTransition);
				break;
		}
	}

	openChangeCaseTypeModal(payload: OrdersButtonClickedPayload): void {
		if (!this.changeCaseTypeModal) {
			this.timberService.warn(
				'OrdersNavigationService: changeCaseTypeModal ref not set; skipping change-case-type action.',
				{ module: 'OrdersNavigationService' }
			);
			return;
		}
		const sub = this.changeCaseTypeModal
			.show(payload.orderId, payload.caseTypeId!)
			.pipe(filter(Boolean))
			.subscribe(() => sub.unsubscribe());
	}

	openExportModal(payload: OrdersButtonClickedPayload): void {
		const request = { orderDetailsId: payload.orderDetailsId!, orderId: payload.orderId };
		if (payload.isOrthoOrder) {
			this.orthoCadExportService.openOrthoCadExportModal(String(payload.caseTypeId), request);
		} else {
			this.restorativeExportService.openRestorativeExportModal(String(payload.caseTypeId), request);
		}
	}

	showVisitReport(orderId: number): void {
		const swOptions = this.globalSettingsService.get()?.companySoftwareOptions ?? [];
		const isNewVisitReportEnabled = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.cswo_NewVisitReport, swOptions
		);
		if (isNewVisitReportEnabled) {
			this.ordersService
				.getBaseOrderData({ orderId })
				.pipe(
					take(1),
					tap((order) => {
						this.shellContextService.updateContext((ctx) => {
							ctx.bizCtx.orderId = order.orderId;
							ctx.bizCtx.patientId = order.patientId;
							ctx.bizCtx.csmMessageId = order.caseStateMessageId;
							ctx.bizCtx.rxId = order.rxId;
						});
						this.router.navigateByUrl(`${STANDARD_REMOTE_URL_PREFIX}/${Consts.visitReportAppId}`);
					}),
					takeUntil(this.componentAlive$)
				)
				.subscribe();
		} else {
			if (!this.visitReportComponent) {
				this.timberService.warn(
					'OrdersNavigationService: visitReportComponent ref not set; skipping visit-report action.',
					{ module: 'OrdersNavigationService' }
				);
				return;
			}
			this.visitReportComponent.show(orderId);
		}
	}

	/** Navigate to Rx with attachments section anchor */
	navigateToRxAttachments(payload: OrdersButtonClickedPayload): void {
		const { rxId, orderId, isTakenByScanner, isReadOnly, statusId } = payload;
		const id = statusId === 10 ? rxId : (orderId ?? rxId);
		const navigateOptions: NavigationExtras = {
			queryParams: {
				isReadOnly: isReadOnly ?? false,
				statusId,
			},
			fragment: 'attachments-section',
			state: { ViewerSourceUrl: this.router.url },
		};
		this.router.navigate(['/rx', id, isTakenByScanner ?? true], navigateOptions);
	}

	/** Download gallery images for an order */
	downloadGalleryImages(orderId: number, rxId?: string): void {
		this.ordersService.downloadGalleryImages(orderId.toString(), rxId);
	}

	/** Send pending attachments for an order */
	sendPendingAttachments(orderId: number): void {
		this.ordersService.sendPendingAttachmentsOrder(orderId)
			.pipe(takeUntil(this.componentAlive$))
			.subscribe({
				error: (error) => {
					this.timberService.error('Error while sending pending attachments', { module: 'OrdersNavigationService', error });
				}
			});
	}

	/** Delete an Rx by guid */
	deleteRx(rxId?: string): void {
		if (!rxId) {
			this.timberService.warn('OrdersNavigationService: deleteRx called without rxId', { module: 'OrdersNavigationService' });
			return;
		}
		this.ordersService.deleteRx(rxId)
			.pipe(takeUntil(this.componentAlive$))
			.subscribe();
	}

	/** Download referral PDF by rxId */
	downloadReferralPdf(rxId?: string): void {
		if (!rxId) {
			this.timberService.warn('OrdersNavigationService: downloadReferralPdf called without rxId', { module: 'OrdersNavigationService' });
			return;
		}
		// TODO: Implement when referral PDF download endpoint is available
		this.timberService.info(
			`OrdersNavigationService: downloadReferralPdf for rxId=${rxId} - not yet implemented`,
			{ module: 'OrdersNavigationService' }
		);
	}

	/** Download screenshots (capture file) for an order */
	downloadScreenshots(orderId: number, patientName?: string, rxId?: string): void {
		this.ordersService.getExportGalleryFile(String(orderId), patientName ?? '', rxId);
	}

	/** Open iTero Design Suite (Exocad Mini App) modal */
	openIteroDesignSuite(payload: OrdersButtonClickedPayload): void {
		if (!this.exocadMiniAppModal) {
			this.timberService.warn(
				'OrdersNavigationService: exocadMiniAppModal ref not set; skipping open-itero-design-suite action.',
				{ module: 'OrdersNavigationService' }
			);
			return;
		}
		this.exocadMiniAppModal.show({
			rxId: payload.rxId!,
			orderId: payload.orderId,
			EnableExocadMiniAppInstallerSSO: this.featuresToggleSettingsService.isVisibleFeature(
				FeatureToggle.EnableExocadMiniAppInstallerSSO,
				false
			),
		}, Pages.Orders);
	}

	/** Download STL design files for an order */
	downloadDesignFiles(orderId: number, rxId?: string): void {
		this.ordersService.downloadStlFlilesOrder(orderId, rxId)
			.pipe(takeUntil(this.componentAlive$), take(1))
			.subscribe();
	}

	/** Open OrthoCad via Windows app protocol link */
	openOrthoCad(payload: OrdersButtonClickedPayload): void {
		if (!payload.openFileLink) {
			this.timberService.warn(
				'OrdersNavigationService: openOrthoCad called without openFileLink in payload',
				{ module: 'OrdersNavigationService' }
			);
			return;
		}
		this.ordersService.startProtocol(payload.openFileLink, true);
		// Show the download link modal with OrthoCad info
		if (this.downloadLinkModal) {
			const orthoCadDownloadLink = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl;
			this.downloadLinkModal.show('OrthoCAD', orthoCadDownloadLink);
		}
	}

	/** Navigate to the resend Rx flow */
	navigateToResend(payload: OrdersButtonClickedPayload): void {
		const { rxId, isTakenByScanner } = payload;
		if (!rxId) {
			this.timberService.warn(
				'OrdersNavigationService: navigateToResend called without rxId',
				{ module: 'OrdersNavigationService' }
			);
			return;
		}
		const navigateOptions: NavigationExtras = {
			state: { ViewerSourceUrl: this.router.url },
		};
		this.router.navigate(['/rx', 'resend', rxId, isTakenByScanner ?? true], navigateOptions);
	}
}